// Typed helpers around Supabase client + edge function calls.
import { supabase, authedFetch } from "./supabase";
import type { Json, Tables } from "@/types/database";

// -----------------------------
// Dashboard / KPIs
// -----------------------------
export async function getDashboardKpis(eventId: string) {
  const [{ count: ordersTotal }, { count: ordersPaid }, { count: tickets }, { count: scanned }, { data: revRow }] = await Promise.all([
    supabase.from("orders").select("id", { head: true, count: "exact" }).eq("event_id", eventId),
    supabase.from("orders").select("id", { head: true, count: "exact" }).eq("event_id", eventId).eq("status", "paid"),
    supabase.from("tickets").select("id", { head: true, count: "exact" }),
    supabase.from("tickets").select("id", { head: true, count: "exact" }).not("checked_in_at", "is", null),
    supabase.from("orders").select("total_cents").eq("event_id", eventId).in("status", ["paid", "invoice_paid"]),
  ]);
  const revenueCents = (revRow ?? []).reduce((s, r) => s + (r.total_cents ?? 0), 0);
  return {
    orders_total: ordersTotal ?? 0,
    orders_paid: ordersPaid ?? 0,
    tickets_issued: tickets ?? 0,
    tickets_scanned: scanned ?? 0,
    revenue_cents: revenueCents,
  };
}

// -----------------------------
// Events
// -----------------------------
export async function listEvents() {
  const { data, error } = await supabase.from("events").select("*").order("starts_at", { ascending: false });
  if (error) throw error;
  return data;
}

export async function getEvent(id: string) {
  const { data, error } = await supabase.from("events").select("*").eq("id", id).single();
  if (error) throw error;
  return data;
}

// -----------------------------
// Ticket types
// -----------------------------
export async function listTicketTypes(eventId: string) {
  const { data, error } = await supabase
    .from("ticket_types")
    .select("*")
    .eq("event_id", eventId)
    .order("sort", { ascending: true });
  if (error) throw error;
  return data;
}

// -----------------------------
// Orders
// -----------------------------
export type OrderListRow = Tables<"orders"> & {
  attendee_count?: number;
  items_summary?: string;
};

export async function listOrders(opts: { eventId?: string; status?: string; flow?: string; q?: string; limit?: number } = {}) {
  let q = supabase
    .from("orders")
    .select("*, items:order_items(qty, ticket_type:ticket_types(name, code))")
    .order("created_at", { ascending: false })
    .limit(opts.limit ?? 200);
  if (opts.eventId) q = q.eq("event_id", opts.eventId);
  if (opts.status) q = q.eq("status", opts.status as Tables<"orders">["status"]);
  if (opts.flow) q = q.eq("flow", opts.flow as Tables<"orders">["flow"]);
  if (opts.q) q = q.or(`order_no.ilike.%${opts.q}%,buyer_email.ilike.%${opts.q}%,buyer_first_name.ilike.%${opts.q}%,buyer_last_name.ilike.%${opts.q}%,buyer_company.ilike.%${opts.q}%`);

  const { data, error } = await q;
  if (error) throw error;
  return data as Array<Tables<"orders"> & { items: Array<{ qty: number; ticket_type: { name: string; code: string } }> }>;
}

export async function getOrder(id: string) {
  // Expliciete FK-namen omdat orders <-> ambassadors en orders <-> discount_codes
  // beide twee FK's hebben (heen + terug). Zonder hint faalt PostgREST embed.
  const { data, error } = await supabase
    .from("orders")
    .select(`
      *,
      items:order_items(*, ticket_type:ticket_types(name, code, price_cents)),
      attendees(*),
      tickets(*, ticket_type:ticket_types(name, code)),
      discount_code:discount_codes!orders_discount_fk(code, kind, value_int),
      ambassador:ambassadors!orders_ambassador_fk(name, email)
    `)
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

export async function sendPaymentLink(orderId: string, customMessage?: string) {
  // Mailt de pay-redirect URL via dedicated payment_link template.
  // Pay-redirect (functions/v1/pay-redirect) maakt zo nodig een verse Mollie payment.
  const { data: order, error } = await supabase
    .from("orders")
    .select("id, order_no, buyer_email, buyer_first_name, total_cents, pay_token")
    .eq("id", orderId)
    .single();
  if (error || !order) throw error ?? new Error("order not found");
  if (!order.pay_token) throw new Error("Order heeft geen pay_token (oude order — maak nieuwe)");

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pay-redirect?t=${order.pay_token}`;
  const intro = customMessage?.trim()
    ? customMessage.trim()
    : "Hierbij je betaallink voor de Haringparty 2026. We hebben je bestelling klaargezet — klik op de knop om je betaling af te ronden.";

  const ctx = {
    voornaam: order.buyer_first_name ?? "",
    bestelnr: order.order_no,
    bedrag: (order.total_cents / 100).toFixed(2).replace(".", ","),
    pay_link: url,
    bericht_intro: intro,
    bericht_extra: "",
  };

  const res = await authedFetch("/email-send", {
    method: "POST",
    body: JSON.stringify({
      template_slug: "payment_link",
      to_email: order.buyer_email,
      context: ctx,
      order_id: order.id,
    }),
  });
  if (!res.ok) throw new Error(`email-send failed: ${res.status} ${await res.text()}`);
  return await res.json();
}

// -----------------------------
// Manual order creation (admin)
// -----------------------------
export interface ManualOrderInput {
  flow: "regular" | "ambassador" | "sponsor";
  event_id: string;
  items: { ticket_type_id: string; qty: number }[];
  buyer: {
    email: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    company?: string;
    vat_number?: string;
    kvk_number?: string;
    billing_address?: {
      street_and_number?: string;
      postal_code?: string;
      city?: string;
      country?: string;
    };
  };
  attendees?: { first_name?: string; last_name?: string; email?: string }[];
  discount_code?: string;
  notes?: string;
  /** "skip_mollie" = cash/bank, geen Mollie payment. Default = "mollie". */
  mode?: "mollie" | "skip_mollie";
}

export async function createManualOrder(input: ManualOrderInput) {
  // admin-create-order routes naar PG checkout_create, en skipt Mollie als mode='skip_mollie'.
  const res = await authedFetch("/admin-create-order", {
    method: "POST",
    body: JSON.stringify({ ...input, source: "admin" }),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Order aanmaken mislukt: ${res.status} ${txt}`);
  }
  return await res.json() as {
    order_id: string;
    order_no: string;
    status: string;
    payment_url?: string;
    total_cents?: number;
  };
}

export interface SponsorInvoiceDetailsInput {
  company: string;
  kvk_number?: string;
  vat_number?: string;
  billing_address: {
    street_and_number: string;
    postal_code: string;
    city: string;
    country?: string;
  };
}

export async function updateSponsorInvoiceDetails(orderId: string, input: SponsorInvoiceDetailsInput) {
  const company = input.company.trim();
  const kvk = input.kvk_number?.trim() || null;
  const vat = input.vat_number?.trim() || null;
  const street = input.billing_address.street_and_number.trim();
  const postal = input.billing_address.postal_code.trim();
  const city = input.billing_address.city.trim();
  const country = (input.billing_address.country?.trim().toUpperCase() || "NL").slice(0, 2);

  if (!company) throw new Error("Bedrijfsnaam is verplicht voor een zakelijke factuur.");
  if (!kvk && !vat) throw new Error("Vul een KvK-nummer of BTW-nummer in voor de Mollie-factuur.");
  if (!street || !postal || !city) throw new Error("Vul straat + huisnummer, postcode en plaats in.");

  const { data, error } = await supabase
    .from("orders")
    .update({
      buyer_company: company,
      kvk_number: kvk,
      vat_number: vat,
      billing_address: {
        street_and_number: street,
        postal_code: postal,
        city,
        country,
      } as Json,
    })
    .eq("id", orderId)
    .in("flow", ["sponsor", "ambassador"])
    .select("id")
    .single();

  if (error) throw error;
  return data;
}

export async function markOrderPaid(orderId: string, notes?: string, paymentMethod?: string) {
  const res = await authedFetch("/order-mark-paid", {
    method: "POST",
    body: JSON.stringify({
      order_id: orderId,
      notes,
      ...(paymentMethod ? { payment_method: paymentMethod } : {}),
    }),
  });
  if (!res.ok) throw new Error(`mark-paid failed: ${res.status} ${await res.text()}`);
  return await res.json() as {
    order_id: string;
    status: string;
    tickets_created: number;
    already_paid?: boolean;
  };
}

export async function refundOrder(orderId: string, amountCents?: number, reason?: string) {
  const res = await authedFetch("/order-refund", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId, amount_cents: amountCents, reason }),
  });
  if (!res.ok) throw new Error(`refund failed: ${res.status} ${await res.text()}`);
  return await res.json() as {
    refund_id: string;
    status: string;
    amount_cents: number;
    full_refund: boolean;
  };
}

export async function syncMollie(orderId: string) {
  const res = await authedFetch("/order-sync-mollie", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId }),
  });
  if (!res.ok) throw new Error(`sync failed: ${res.status} ${await res.text()}`);
  return await res.json() as {
    order_id: string;
    status: string;
    mollie_status: string;
    action: string;
  };
}

export async function syncMollieInvoice(orderId: string) {
  const res = await authedFetch("/order-sync-invoice", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId }),
  });
  if (!res.ok) throw new Error(`invoice sync failed: ${res.status} ${await res.text()}`);
  return await res.json() as {
    order_id: string;
    order_no?: string;
    status?: string;
    sales_invoice_id: string;
    mollie_invoice_status: string;
    invoice_no?: string | null;
    action: string;
  };
}

export async function refulfillOrder(orderId: string) {
  const res = await authedFetch("/admin-refulfill", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId }),
  });
  if (!res.ok) throw new Error(`refulfill failed: ${res.status} ${await res.text()}`);
  return await res.json() as {
    order_id: string;
    tickets_created: number;
    ambassador_linked: string | null;
  };
}

export type AdminTicketLink = {
  order_id: string;
  order_no: string;
  ticket_id: string | null;
  expires_at: string;
  ticket_url: string;
  order_pdf_url: string;
  ticket_pdf_url: string | null;
  pdf_url: string;
};

export async function createAdminTicketLink(args: { orderId?: string; ticketId?: string }) {
  const res = await authedFetch("/admin-ticket-link", {
    method: "POST",
    body: JSON.stringify({
      order_id: args.orderId,
      ticket_id: args.ticketId,
    }),
  });
  if (!res.ok) throw new Error(`ticket-link failed: ${res.status} ${await res.text()}`);
  return await res.json() as AdminTicketLink;
}

/**
 * Aggregate payment-method counts voor laatste N dagen — gebruikt door dashboard widget.
 * Gebruikt orders.metadata->>'payment_method' (door mollie-webhook bij paid event geschreven).
 */
export async function getPaymentMethodStats(eventId: string, days = 30) {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const { data, error } = await supabase
    .from("orders")
    .select("metadata, total_cents")
    .eq("event_id", eventId)
    .in("status", ["paid", "invoice_paid"])
    .gte("paid_at", since)
    .limit(1000);
  if (error) throw error;

  const counts: Record<string, { count: number; cents: number }> = {};
  for (const r of (data ?? []) as Array<{ metadata: Record<string, unknown> | null; total_cents: number }>) {
    const m = (r.metadata?.payment_method as string | null | undefined) ?? "onbekend";
    if (!counts[m]) counts[m] = { count: 0, cents: 0 };
    counts[m].count++;
    counts[m].cents += r.total_cents;
  }
  return Object.entries(counts)
    .map(([method, v]) => ({ method, count: v.count, cents: v.cents }))
    .sort((a, b) => b.count - a.count);
}

// -----------------------------
// Ambassadors
// -----------------------------
export interface AmbassadorRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  pin_status: string;
  redeem_token: string | null;
  notes: string | null;
  created_at: string;
  discount_code: {
    id: string;
    code: string;
    used_count: number;
    max_uses: number | null;
    max_tickets_per_redemption?: number | null;
  } | null;
  // Computed (via Promise.all hieronder)
  own_order_status?: string | null;
  own_order_id?: string | null;
  own_order_no?: string | null;
  own_ticket_count?: number;   // aantal eigen toegangstickets (0 = komt aanlopen, geen scan)
  redeemed_extra_tickets?: number;
  redeemed_extra_revenue_cents?: number;
}

/** Zet de speldje-status van een ambassadeur (RLS: admin only). pending|sent|delivered. */
export async function setAmbassadorPinStatus(ambassadorId: string, status: "pending" | "sent" | "delivered"): Promise<void> {
  const { data, error } = await supabase
    .from("ambassadors")
    .update({ pin_status: status })
    .eq("id", ambassadorId)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) throw new Error("Niet opgeslagen — geen rechten of ambassadeur niet gevonden");
}

export async function listAmbassadors(opts: { includeRevenue?: boolean } = {}): Promise<AmbassadorRow[]> {
  // includeRevenue=false → géén omzet ophalen (deur-/medewerker-weergave: niets financieels in de payload).
  const includeRevenue = opts.includeRevenue !== false;
  // Stap 1: ambassadeurs + hun discount_code
  const { data: ambs, error } = await supabase
    .from("ambassadors")
    .select("id, name, email, phone, pin_status, redeem_token, notes, created_at, discount_code:discount_codes!ambassadors_discount_fk(id, code, used_count, max_uses, max_tickets_per_redemption)")
    .order("created_at", { ascending: false });
  if (error) throw error;
  if (!ambs || ambs.length === 0) return [];

  const ambIds = ambs.map((a) => a.id);
  const codeIds = ambs.map((a) => (a.discount_code as unknown as { id: string } | null)?.id).filter(Boolean) as string[];

  // Stap 2: orders waar deze ambassadeur de koper was (eigen ambassadeurschap via ambassador-flow).
  // total_cents bewust NIET ophalen — niet nodig voor de lijst en het is financiële data.
  const { data: ownOrders } = await supabase
    .from("orders")
    .select("id, order_no, status, ambassador_id")
    .in("ambassador_id", ambIds);

  // Stap 2b: hoeveel eigen toegangstickets heeft elke ambassadeur (0 = komt aanlopen)
  const ownOrderIds = (ownOrders ?? []).map((o) => o.id);
  const { data: ownTickets } = ownOrderIds.length > 0 ? await supabase
    .from("tickets")
    .select("order_id")
    .in("order_id", ownOrderIds) : { data: [] as Array<{ order_id: string }> };
  const ticketCountByOrder = new Map<string, number>();
  for (const t of ownTickets ?? []) ticketCountByOrder.set(t.order_id, (ticketCountByOrder.get(t.order_id) ?? 0) + 1);

  // Stap 3: redemptions van hun discount-codes (= extra tickets via vrienden).
  // Bij includeRevenue=false halen we GEEN order-totalen op (geen financiële data in de payload).
  type RedRow = { code_id: string; order_id: string | null; order?: { total_cents: number } | null };
  let redemptions: RedRow[] = [];
  if (codeIds.length > 0) {
    if (includeRevenue) {
      const { data } = await supabase
        .from("discount_redemptions")
        .select("code_id, order_id, order:orders(total_cents)")
        .in("code_id", codeIds);
      redemptions = (data ?? []) as unknown as RedRow[];
    } else {
      const { data } = await supabase
        .from("discount_redemptions")
        .select("code_id, order_id")
        .in("code_id", codeIds);
      redemptions = (data ?? []) as unknown as RedRow[];
    }
  }

  // Aggregate per ambassador
  return ambs.map((a) => {
    const ownOrder = (ownOrders ?? []).find((o) => o.ambassador_id === a.id);
    const dc = a.discount_code as unknown as { id: string; code: string; used_count: number; max_uses: number | null; max_tickets_per_redemption?: number | null } | null;
    const reds = (redemptions ?? []).filter((r) => r.code_id === dc?.id);
    return {
      id: a.id,
      name: a.name,
      email: a.email,
      phone: a.phone,
      pin_status: a.pin_status,
      redeem_token: a.redeem_token,
      notes: a.notes,
      created_at: a.created_at,
      discount_code: dc,
      own_order_status: ownOrder?.status ?? null,
      own_order_id: ownOrder?.id ?? null,
      own_order_no: ownOrder?.order_no ?? null,
      own_ticket_count: ownOrder ? (ticketCountByOrder.get(ownOrder.id) ?? 0) : 0,
      redeemed_extra_tickets: reds.length,
      redeemed_extra_revenue_cents: includeRevenue
        ? reds.reduce((s, r) => s + (r.order?.total_cents ?? 0), 0)
        : 0,
    } as AmbassadorRow;
  });
}

export async function inviteAmbassador(input: {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  send_email?: boolean;
}) {
  const res = await authedFetch("/ambassador-invite", {
    method: "POST",
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`invite failed: ${res.status} ${await res.text()}`);
  return await res.json() as {
    ambassador_id: string;
    name: string;
    email: string;
    code: string;
    redeem_url: string;
    email_sent: boolean;
    email_error: string | null;
  };
}

// -----------------------------
// Tickets
// -----------------------------
export async function listTickets(opts: { status?: string; q?: string; limit?: number } = {}) {
  let q = supabase
    .from("tickets")
    .select(`
      *,
      attendee:attendees(first_name, last_name, email),
      ticket_type:ticket_types(name, code),
      order:orders(order_no, buyer_email, flow, ambassador:ambassadors!orders_ambassador_fk(name, email))
    `)
    .order("created_at", { ascending: false })
    .limit(opts.limit ?? 2000);
  if (opts.status) q = q.eq("status", opts.status as Tables<"tickets">["status"]);
  const { data, error } = await q;
  if (error) throw error;
  if (opts.q) {
    const needle = opts.q.toLowerCase();
    return (data ?? []).filter((t) => {
      const a = t.attendee as { first_name?: string; last_name?: string; email?: string } | null;
      return (
        t.ticket_no.toLowerCase().includes(needle) ||
        (a?.first_name ?? "").toLowerCase().includes(needle) ||
        (a?.last_name ?? "").toLowerCase().includes(needle) ||
        (a?.email ?? "").toLowerCase().includes(needle)
      );
    });
  }
  return data;
}

export async function voidTicket(ticketId: string) {
  const { error } = await supabase.from("tickets").update({ status: "void" }).eq("id", ticketId);
  if (error) throw error;
}

// -----------------------------
// Sponsors
// -----------------------------
export async function listSponsors(eventId: string) {
  const { data, error } = await supabase.from("sponsors").select("*").eq("event_id", eventId).order("sort", { ascending: true });
  if (error) throw error;
  return data;
}

// -----------------------------
// Email templates / campaigns
// -----------------------------
export async function listTemplates() {
  const { data, error } = await supabase.from("email_templates").select("*").order("name");
  if (error) throw error;
  return data;
}

export async function getTemplate(slug: string) {
  const { data, error } = await supabase.from("email_templates").select("*").eq("slug", slug).single();
  if (error) throw error;
  return data;
}

export async function saveTemplate(t: Pick<Tables<"email_templates">, "id" | "name" | "subject" | "html" | "trigger" | "active">) {
  const { error } = await supabase
    .from("email_templates")
    .update({ name: t.name, subject: t.subject, html: t.html, trigger: t.trigger, active: t.active })
    .eq("id", t.id);
  if (error) throw error;
}

export async function listCampaigns() {
  const { data, error } = await supabase
    .from("email_campaigns")
    .select("*, template:email_templates(name, slug)")
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return data;
}

export type BroadcastRecipient = {
  email: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  company?: string;
  status?: string;
  source?: string;
  context?: Record<string, unknown>;
};

export type EmailAttachment = {
  filename: string;
  content: string;
  content_type?: string;
};

export type BroadcastAttachment = EmailAttachment;

export type BroadcastResult = {
  campaign_id: string;
  recipient_count: number;
  sent?: number;
  failed?: number;
  dry_run?: boolean;
  sample?: string[];
  invalid_count?: number;
  duplicate_count?: number;
};

export async function broadcast(args: {
  template_slug?: string;
  subject?: string;
  html?: string;
  segment?: { flow?: string; status?: string; event_id?: string };
  recipients?: BroadcastRecipient[];
  attachments?: BroadcastAttachment[];
  source?: string;
  campaign_name?: string;
  dry_run?: boolean;
}): Promise<BroadcastResult> {
  const res = await authedFetch("/email-broadcast", { method: "POST", body: JSON.stringify(args) });
  if (!res.ok) throw new Error(`broadcast failed: ${res.status} ${await res.text()}`);
  return await res.json();
}

// -----------------------------
// Email sends (outbound) — alle verzonden mails per ontvanger
// -----------------------------
export type EmailSendListRow = Tables<"email_sends"> & {
  campaign?: { name: string } | null;
  order?: { order_no: string; buyer_email: string } | null;
};

export async function listEmailSends(opts: {
  q?: string;
  status?: string;
  templateSlug?: string;
  orderId?: string;
  campaignId?: string;
  limit?: number;
} = {}) {
  let q = supabase
    .from("email_sends")
    .select(`
      *,
      campaign:email_campaigns(name),
      order:orders(order_no, buyer_email)
    `)
    .order("created_at", { ascending: false })
    .limit(opts.limit ?? 200);
  if (opts.status) q = q.eq("status", opts.status);
  if (opts.templateSlug) q = q.eq("template_slug", opts.templateSlug);
  if (opts.orderId) q = q.eq("order_id", opts.orderId);
  if (opts.campaignId) q = q.eq("campaign_id", opts.campaignId);
  if (opts.q) q = q.or(`to_email.ilike.%${opts.q}%,subject.ilike.%${opts.q}%`);
  const { data, error } = await q;
  if (error) throw error;
  return data as unknown as EmailSendListRow[];
}

export async function getEmailSend(id: string) {
  const { data, error } = await supabase
    .from("email_sends")
    .select(`
      *,
      campaign:email_campaigns(id, name, segment),
      order:orders(id, order_no, buyer_email, buyer_first_name, buyer_last_name, total_cents, status, flow)
    `)
    .eq("id", id)
    .single();
  if (error) throw error;

  // template_slug is text (no FK) — resolve template separately
  let template: { id: string; name: string; slug: string; subject: string } | null = null;
  if (data.template_slug) {
    const { data: tpl } = await supabase
      .from("email_templates")
      .select("id, name, slug, subject")
      .eq("slug", data.template_slug)
      .maybeSingle();
    template = tpl ?? null;
  }
  return { ...data, template };
}

export async function listEmailEvents(emailSendId: string) {
  const { data, error } = await supabase
    .from("email_events")
    .select("*")
    .eq("email_send_id", emailSendId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data;
}

export async function resendEmail(emailSendId: string) {
  // Refire by reading the existing send and calling email-send again.
  const { data: prev, error } = await supabase
    .from("email_sends")
    .select("template_slug, to_email, order_id, ticket_id")
    .eq("id", emailSendId)
    .single();
  if (error || !prev) throw error ?? new Error("send not found");
  if (!prev.template_slug) throw new Error("Geen template_slug — kan niet opnieuw verzenden");

  const res = await authedFetch("/email-send", {
    method: "POST",
    body: JSON.stringify({
      template_slug: prev.template_slug,
      to_email: prev.to_email,
      order_id: prev.order_id,
      ticket_id: prev.ticket_id,
      context: {},
    }),
  });
  if (!res.ok) throw new Error(`resend failed: ${res.status} ${await res.text()}`);
  return await res.json();
}

// -----------------------------
// Email inbound (binnenkomende mail)
// -----------------------------
export type EmailInboundListRow = Tables<"email_inbound"> & {
  order?: { id: string; order_no: string; buyer_email: string } | null;
};

export async function listInbound(opts: { unread?: boolean; q?: string; limit?: number } = {}) {
  let q = supabase
    .from("email_inbound")
    .select(`
      *,
      order:orders!email_inbound_matched_order_id_fkey(id, order_no, buyer_email)
    `)
    .order("received_at", { ascending: false })
    .limit(opts.limit ?? 100);
  if (opts.unread) q = q.is("read_at", null).is("archived_at", null);
  if (opts.q) q = q.or(`from_email.ilike.%${opts.q}%,subject.ilike.%${opts.q}%`);
  const { data, error } = await q;
  if (error) throw error;
  return data as EmailInboundListRow[];
}

export async function getInbound(id: string) {
  const { data, error } = await supabase
    .from("email_inbound")
    .select(`
      *,
      order:orders!email_inbound_matched_order_id_fkey(id, order_no, buyer_email, buyer_first_name, buyer_last_name, status, flow, total_cents),
      original:email_sends!email_inbound_matched_email_send_id_fkey(id, subject, template_slug, sent_at)
    `)
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
}

export async function markInboundRead(id: string, read = true) {
  const { error } = await supabase
    .from("email_inbound")
    .update({ read_at: read ? new Date().toISOString() : null })
    .eq("id", id);
  if (error) throw error;
}

export async function replyInbound(inboundId: string, body: { body_text?: string; body_html?: string; subject?: string; attachments?: EmailAttachment[] }) {
  const res = await authedFetch("/email-reply", {
    method: "POST",
    body: JSON.stringify({ inbound_id: inboundId, ...body }),
  });
  if (!res.ok) throw new Error(`reply failed: ${res.status} ${await res.text()}`);
  return await res.json() as { id: string; email_send_id: string; status: string; attachment_count?: number };
}

export async function archiveInbound(id: string) {
  const { error } = await supabase
    .from("email_inbound")
    .update({ archived_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function backfillInbound(opts: { id?: string; limit?: number } = {}) {
  const res = await authedFetch("/email-inbound-backfill", {
    method: "POST",
    body: JSON.stringify(opts),
  });
  if (!res.ok) throw new Error(`backfill failed: ${res.status} ${await res.text()}`);
  return await res.json() as { processed: number; updated: number; failed: number };
}

export async function inboxUnreadCount() {
  const { count, error } = await supabase
    .from("email_inbound")
    .select("id", { head: true, count: "exact" })
    .is("read_at", null)
    .is("archived_at", null);
  if (error) throw error;
  return count ?? 0;
}

// -----------------------------
// Scanner
// -----------------------------
/** Ambassadeur-info bij een gescand ticket — voor speldje-herkenning aan de deur. */
export interface ScanAmbassador {
  id: string;
  name: string;
  pin_status: string; // pending | sent | delivered
  is_self: boolean;   // bezoeker op dit ticket is de ambassadeur zelf (krijgt speldje)
}

export async function validateQr(qr: string, deviceId?: string) {
  // Harde timeout: aan de deur op haperende wifi mag een hangende fetch de scanner niet bevriezen.
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 8000);
  let res: Response;
  try {
    res = await authedFetch("/qr-validate", {
      method: "POST",
      body: JSON.stringify({ qr, device_id: deviceId }),
      signal: ctrl.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error("Geen verbinding — probeer opnieuw");
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw new Error(`qr-validate ${res.status}`);
  return await res.json() as
    | { result: "ok"; ticket: { id: string; ticket_no: string }; attendee: { first_name?: string; last_name?: string }; ticket_type: { name: string; code: string }; order: { order_no: string }; ambassador: ScanAmbassador | null }
    | { result: "already_scanned"; scanned_at: string; ticket: { id: string; ticket_no: string }; attendee: { first_name?: string; last_name?: string }; ticket_type: { name: string; code: string }; ambassador: ScanAmbassador | null }
    | { result: "invalid"; reason: string };
}

/** Markeer het speldje van een ambassadeur als uitgereikt (RLS: admin only). */
export async function markPinDelivered(ambassadorId: string): Promise<void> {
  const { data, error } = await supabase
    .from("ambassadors")
    .update({ pin_status: "delivered" })
    .eq("id", ambassadorId)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Speldje niet opgeslagen — geen rechten of ambassadeur niet gevonden");
  }
}

/** Live tellers: hoeveel binnen (gescand) / nog niet / geblokkeerd / totaal. */
export async function ticketStatusCounts(): Promise<{ scanned: number; issued: number; voided: number; total: number }> {
  const { data, error } = await supabase.from("tickets").select("status").limit(5000);
  if (error) throw error;
  let scanned = 0, issued = 0, voided = 0;
  for (const t of data ?? []) {
    if (t.status === "scanned") scanned++;
    else if (t.status === "void") voided++;
    else issued++;
  }
  return { scanned, issued, voided, total: (data ?? []).length };
}

/** Rij voor de naam-zoek/incheck aan de deur. */
export interface DoorListRow {
  ticket_id: string;
  ticket_no: string;
  name: string;
  ticket_type: string;
  status: string;            // issued | scanned | void
  checked_in_at: string | null;
  ambassador_name: string | null;
}

/** Laad alle tickets (naam, ticketnr, status, ambassadeur) voor zoeken-op-naam in de scanner. */
export async function loadDoorList(): Promise<DoorListRow[]> {
  const { data, error } = await supabase
    .from("tickets")
    .select(`id, ticket_no, status, checked_in_at,
      attendee:attendees(first_name, last_name),
      ticket_type:ticket_types(name),
      order:orders(ambassador:ambassadors!orders_ambassador_fk(name))`)
    .limit(2000);
  if (error) throw error;
  const first = <T,>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);
  return (data ?? []).map((t) => {
    const a = first(t.attendee as unknown) as { first_name?: string; last_name?: string } | null;
    const tt = first(t.ticket_type as unknown) as { name?: string } | null;
    const ord = first(t.order as unknown) as { ambassador?: unknown } | null;
    const amb = first(ord?.ambassador) as { name?: string } | null;
    return {
      ticket_id: t.id,
      ticket_no: t.ticket_no,
      name: `${a?.first_name ?? ""} ${a?.last_name ?? ""}`.trim() || "Gast",
      ticket_type: tt?.name ?? "",
      status: t.status,
      checked_in_at: t.checked_in_at,
      ambassador_name: amb?.name ?? null,
    };
  });
}

/**
 * Manual ticket lookup voor de scanner — fallback als QR niet leesbaar is.
 * Zoekt op ticket_no en doet dezelfde atomic check-in als qr-validate.
 */
export async function checkInByTicketNo(ticketNo: string, deviceId?: string) {
  const cleaned = ticketNo.trim().toUpperCase();
  const { data: ticket, error } = await supabase
    .from("tickets")
    .select(`id, ticket_no, status, checked_in_at,
      attendee:attendees(first_name, last_name, email),
      ticket_type:ticket_types(name, code),
      order:orders(id, order_no, buyer_email, flow, ambassador:ambassadors!orders_ambassador_fk(id, name, pin_status, email))`)
    .eq("ticket_no", cleaned)
    .maybeSingle();

  if (error) throw error;
  if (!ticket) return { result: "invalid" as const, reason: "Ticketnr niet gevonden" };
  if (ticket.status === "void") return { result: "invalid" as const, reason: "Ticket is geblokkeerd" };

  const ambassador = await buildScanAmbassador(ticket);

  if (ticket.checked_in_at) {
    return {
      result: "already_scanned" as const,
      scanned_at: ticket.checked_in_at,
      ticket: { id: ticket.id, ticket_no: ticket.ticket_no },
      attendee: ticket.attendee as unknown as { first_name?: string; last_name?: string } | null,
      ticket_type: ticket.ticket_type as unknown as { name: string; code: string } | null,
      ambassador,
    };
  }

  const { data: scanned, error: upErr } = await supabase
    .from("tickets")
    .update({ checked_in_at: new Date().toISOString(), status: "scanned" })
    .eq("id", ticket.id)
    .is("checked_in_at", null)
    .select("checked_in_at")
    .maybeSingle();

  if (upErr) throw upErr;
  if (!scanned) {
    const { data: refreshed } = await supabase
      .from("tickets")
      .select("checked_in_at")
      .eq("id", ticket.id)
      .single();
    return {
      result: "already_scanned" as const,
      scanned_at: refreshed?.checked_in_at ?? new Date().toISOString(),
      ticket: { id: ticket.id, ticket_no: ticket.ticket_no },
      attendee: ticket.attendee as unknown as { first_name?: string; last_name?: string } | null,
      ticket_type: ticket.ticket_type as unknown as { name: string; code: string } | null,
      ambassador,
    };
  }

  await supabase.from("scan_logs").insert({
    ticket_id: ticket.id, result: "ok", device_id: deviceId,
  });

  return {
    result: "ok" as const,
    ticket: { id: ticket.id, ticket_no: ticket.ticket_no },
    attendee: ticket.attendee as unknown as { first_name?: string; last_name?: string } | null,
    ticket_type: ticket.ticket_type as unknown as { name: string; code: string } | null,
    order: ticket.order as unknown as { order_no: string } | null,
    ambassador,
  };
}

// PostgREST kan een to-one embed soms als array teruggeven; pak het eerste element.
function firstEmbed<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return (value[0] as T) ?? null;
  return (value ?? null) as T | null;
}

/**
 * Leid ambassadeur-info af uit een gejoind ticket (manuele incheck-pad).
 * is_self = de bezoeker op dit ticket is de ambassadeur zelf, dus krijgt het speldje.
 * Robuust: e-mailmatch óf de ambassadeur-bestelling heeft maar 1 ticket.
 */
async function buildScanAmbassador(ticket: unknown): Promise<ScanAmbassador | null> {
  const t = ticket as { order?: unknown; attendee?: unknown };
  const order = firstEmbed(t.order) as { id?: string; ambassador?: unknown } | null;
  const amb = firstEmbed(order?.ambassador) as
    | { id?: string; name?: string; pin_status?: string; email?: string }
    | null;
  if (!amb || !amb.id) return null;

  const attendee = firstEmbed(t.attendee) as { email?: string } | null;
  const attEmail = (attendee?.email ?? "").trim().toLowerCase();
  const ambEmail = (amb.email ?? "").trim().toLowerCase();
  let isSelf = attEmail.length > 0 && attEmail === ambEmail;

  if (!isSelf && order?.id) {
    const { count } = await supabase
      .from("tickets")
      .select("id", { count: "exact", head: true })
      .eq("order_id", order.id);
    if (count === 1) isSelf = true;
  }

  return {
    id: amb.id,
    name: amb.name ?? "",
    pin_status: amb.pin_status ?? "pending",
    is_self: isSelf,
  };
}

/** Re-sign alle tickets (of gefilterd op ticket_ids). Eenmalig nodig na bug-fix. */
export async function resignTickets(ticketIds?: string[]) {
  const res = await authedFetch("/admin-resign-tickets", {
    method: "POST",
    body: JSON.stringify(ticketIds ? { ticket_ids: ticketIds } : {}),
  });
  if (!res.ok) throw new Error(`resign failed: ${res.status} ${await res.text()}`);
  return await res.json() as { resigned: number; failed: number; total: number };
}

// -----------------------------
// Sponsor invoice
// -----------------------------
export async function sendSponsorInvoice(orderId: string, dueInDays = 30) {
  const res = await authedFetch("/sponsor-invoice", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId, due_in_days: dueInDays }),
  });
  if (!res.ok) {
    const txt = await res.text();
    let message = `sponsor-invoice ${res.status}: ${txt}`;
    try {
      const body = JSON.parse(txt) as { error?: string; details?: string };
      const details = body.details ?? body.error ?? txt;
      if (details.includes("recipient.organizationNumber") || details.includes("recipient.vatNumber")) {
        message = "Mollie vereist voor een zakelijke factuur een KvK-nummer of BTW-nummer. Vul de factuurgegevens aan en probeer opnieuw.";
      } else if (body.error) {
        message = body.details ? `${body.error}: ${body.details}` : body.error;
      }
    } catch {
      // keep raw fallback
    }
    throw new Error(message);
  }
  return await res.json() as {
    ok: boolean;
    invoice_no?: string | null;
    invoice_due_at?: string | null;
    mollie_sales_invoice_id?: string | null;
    mollie_sales_invoice_status?: string | null;
    draft_reason?: string | null;
    already_exists?: boolean;
    already_paid?: boolean;
  };
}
